/**
 * This file contains many functions that make calls to our API and return the promises from those calls.
 */
import queryString from 'qs';
import { flatMap, range } from 'lodash';
import Api from '../api';
import { ContactFormData, Handle, Vendor } from './vendor';
import { TOKEN_STORAGE_KEY } from '../../Containers/App/reducer';
import toast from '../../Components/Toast/Toast';

const INFLUENCER = '/influencers';

export interface Influencer {
  email: string;
  first_name: string;
  id: number;
  last_logged_in_at: string;
  last_name: string;
  phone: string | null;
  phone_country: string;
  registered_at: string | null;
  role: string;
  title: string | null;
  vendor: Vendor;
  vendor_id: string;
  vendor_name: string | null;
}

const InfluencerAPI = {
  /**
   *
   * @param {string} params - query params
   *
   * @returns {Promise} response:
   * example: {
   *   vendors: [{
   *     ...vendor
   *   }]
   * }
   */
  async getAll(params = {}) {
    return Api.get(`${INFLUENCER}?${queryString.stringify(params)}`);
  },

  /**
   * Gets the social media team
   *
   *
   * @returns {Promise} response:
   * example: {
   *   vendors: [{
   *     ...vendor
   *   }]
   * }
   */
  async getTeam() {
    return Api.get(`${INFLUENCER}/team?limit=1000`);
  },

  /**
   * Get influencers for export
   *
   * @returns {promise} promise returned from api call that eventually
   * resolves to an array of influencers.
   */
  async getInfluencersExport() {
    const limit = 100;
    const firstPage = await Api.get(
      `${INFLUENCER}?${queryString.stringify({
        page: 1,
      })}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const res = await Promise.all(
      range(totalPages).map(async (page) => {
        const params = queryString.stringify({ page: page + 1, limit });
        return Api.get(`${INFLUENCER}?${params}`);
      }),
    );

    const influencers = flatMap(res, 'rows');

    // Duplicate line for each handle
    return influencers.reduce((acc, influencer) => {
      if (
        !influencer.vendor.influencer_handles ||
        !influencer.vendor.influencer_handles.length
      ) {
        return [
          ...acc,
          {
            ...influencer,
            vendor: { ...influencer.vendor, influencer_primary_handle: true },
          },
        ];
      }
      const rows = influencer.vendor.influencer_handles.map(
        (handle: Handle) => ({
          ...influencer,
          vendor: {
            ...influencer.vendor,
            influencer_handle: handle.social_media_handle,
            influencer_platform: handle.social_media_platform,
            influencer_primary_handle: handle.primary,
          },
        }),
      );
      return [...acc, ...rows];
    }, []);
  },

  /**
   * Archives or unarchives an influencer
   * @param {number} id - The influencer ID
   * @param {boolean} archive - Whether to archive (true) or unarchive (false)
   * @returns {Promise} response
   */
  async archiveInfluencer(id: number, archive: boolean) {
    const action = archive ? 'archived' : 'unarchived';
    const endpoint = `${INFLUENCER}/${id}/${action === 'archived' ? 'archive' : 'unarchive'}`;
    try {
      const response = await Api.put(endpoint);
      const status = response.originalStatus;

      toast(
        {
          title: `Influencer has been ${action}`,
          text: !archive ? `Status has been reset to ${status}` : '',
        },
        {
          type: 'success',
          autoClose: 3000,
          style: {
            whiteSpace: 'normal',
          },
        },
      );
      return response;
    } catch (error: any) {
      const errorAction = archive ? 'archive' : 'unarchive';
      toast(`Failed to ${errorAction} influencer.`, {
        type: 'error',
        autoClose: 3000,
      });
      console.error('Archive error:', error);
      throw error;
    }
  },

  submitContactForm(data: ContactFormData) {
    const formData = new FormData();
    formData.append(
      'data',
      JSON.stringify({
        user: data.user,
        flow: data.flow,
        description: data.description,
      }),
    );
    data.files.forEach((file) => {
      formData.append(`files`, file);
    });
    return Api.post('/influencers/contact', {
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Auth-Token': localStorage.getItem(TOKEN_STORAGE_KEY),
      },
    });
  },
};

export default InfluencerAPI;
